import { Divider } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useCopyToClipboard } from 'react-use'

import { API } from '@/api'
import { ServerImageInfo, ServerScaleSpecInfo, ServerSnapInfo } from '@/api/type-server.ts'
import CopyIcon from '@/assets/icons/copy.svg?react'
import { Loading, message, Trans } from '@/components'
import { useUnifiedContext } from '@/contexts/UnifiedContext'
import { formatRamMB } from '@/utils/formatter.ts'
import { fetchAllPages } from '@/utils/pagination'

interface ScalingSpecDetailProp {
  scalingSpecResId: string
}

export default function ScalingSpecTarget({ scalingSpecResId }: ScalingSpecDetailProp) {
  const ctx = useUnifiedContext()
  const [loading, setLoading] = useState(false)
  const [scaleSpec, setScaleSpec] = useState<ServerScaleSpecInfo>()
  const [imageSpec, setImageSpec] = useState<ServerImageInfo>()
  const [snapshotSpec, setSnapshotSpec] = useState<ServerSnapInfo>()
  const [domains, setDomains] = useState<number[]>([])
  const [state, copyToClipboard] = useCopyToClipboard()

  // Fetch domains on mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await API.getDomainList()
        if (res.code === 0) {
          setDomains(res.domainInfos?.map((d) => d.domain) || [])
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error)
      }
    }
    fetchDomains()
  }, [])

  const getScaleSpec = useCallback(
    async (specId: string) => {
      setLoading(true)
      try {
        const res = await API.getScaleSpec({
          spec_id: specId,
        })
        if (res.code === 0) {
          setScaleSpec(res.spec)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [scalingSpecResId],
  )

  const getImages = useCallback(
    async (imageId: number) => {
      setLoading(true)
      try {
        const images = await fetchAllPages({
          fetchPage: (page, pageSize) => API.getImageList({ page, per_page: pageSize }),
          getItems: (response) => response.images,
        })
        setImageSpec(images.find((image) => image.id === imageId))
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [scalingSpecResId],
  )

  const getSnapshots = useCallback(
    async (snapshotId: number) => {
      setLoading(true)
      try {
        // Try platform snapshots first
        const snapshots = await fetchAllPages({
          fetchPage: (page, pageSize) => API.getSnapList({ page, per_page: pageSize }),
          getItems: (response) => response.snapshots,
        })
        const snapshot = snapshots.find((item) => Number(item.id) === snapshotId)
        if (snapshot) {
          setSnapshotSpec(snapshot)
          return
        }

        // Try team shared snapshots if in team context
        if (ctx.teamResID && domains.length > 0) {
          const teamSnapshots = await fetchAllPages({
            fetchPage: (page, pageSize) =>
              API.getTeamSharedSnapshots({
                page,
                per_page: pageSize,
                res_id: ctx.teamResID!,
                regions: domains,
              }),
            getItems: (response) => response.snapshots,
          })
          const teamSnapshot = teamSnapshots.find((item) => Number(item.snapshot.id) === snapshotId)
          if (teamSnapshot) {
            setSnapshotSpec(teamSnapshot.snapshot)
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [scalingSpecResId, ctx.teamResID, domains],
  )

  useEffect(() => {
    getScaleSpec(scalingSpecResId)
  }, [getScaleSpec, scalingSpecResId])

  useEffect(() => {
    if (scaleSpec) {
      const { fromImageId, fromSnapshotId } = scaleSpec.runtimeEnvs[0].systemEnv
      if (fromSnapshotId !== 0) {
        getSnapshots(fromSnapshotId)
      }
      if (fromImageId !== 0) {
        getImages(fromImageId)
      }
    }
  }, [getImages, getSnapshots, scaleSpec])

  useEffect(() => {
    if (state.value) {
      message.success('Copied')
    }
  }, [state])

  return (
    <>
      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && scaleSpec && (
        <div className="p-4 bg-panel-gray-100 rounded-lg flex flex-wrap t-sm text-tertiary gap-y-4">
          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="scalingTarget.instancePreview" ns="scaling">
                Instance Preview
              </Trans>
            </div>
            <Divider className="my-0" />
          </div>

          <div className="grid grid-cols-4 gap-y-4 bg-panel-gray-100 rounded-lg">
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.image" ns="scaling">
                    Image
                  </Trans>
                </div>
                <div className="t-sb text-primary">{imageSpec?.alias}</div>
              </div>
              <div className="col-span-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.imageDesc" ns="scaling">
                    Image description
                  </Trans>
                </div>
                <div className="t-sb text-primary">{imageSpec?.description}</div>
              </div>
              {snapshotSpec && (
                <div className="col-span-1">
                  <div className="t-xs text-secondary">
                    <Trans i18nKey="scalingTarget.snapshot" ns="scaling">
                      Snapshot
                    </Trans>
                  </div>
                  <div className="t-sb text-primary">{snapshotSpec.flag?.subject || '-'}</div>
                </div>
              )}
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.gpuModel" ns="scaling">
                    GPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.gpuName}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.gpus" ns="scaling">
                    GPUs
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.gpus}
                </div>
              </div>
              <div className="col-span-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.gpuRam" ns="scaling">
                    GPU RAM
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(scaleSpec.runtimeEnvs[0].hardwareEnv.resource.gpuRamMB)}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.cpuModel" ns="scaling">
                    CPU Model
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.cpuName}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.vCpus" ns="scaling">
                    vCPUs
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.cpus}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.ram" ns="scaling">
                    RAM
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {formatRamMB(scaleSpec.runtimeEnvs[0].hardwareEnv.resource.ramMB)}
                </div>
              </div>
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.storage" ns="scaling">
                    Storage
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].hardwareEnv.resource.diskGB} GB
                </div>
              </div>
            </div>
          </div>

          <div className="w-full t-xs text-tertiary flex space-x-2 items-center overflow-hidden">
            <div className="text-nowrap">
              <Trans i18nKey="scalingTarget.serviceAndStartCommand" ns="scaling">
                Service & Start Command
              </Trans>
            </div>
            <Divider className="my-0" />
          </div>

          <div className="w-full grid grid-cols-4 gap-y-4 bg-panel-gray-100 rounded-lg">
            <div className="col-span-6 grid grid-cols-6 space-x-2">
              <div className="col-span-1">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.protocol" ns="scaling">
                    Protocol
                  </Trans>
                </div>
                <div className="t-sb text-primary">
                  {scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.length > 0 ? 'Http' : '-'}
                </div>
              </div>
              <div className="col-span-2">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.port" ns="scaling">
                    Port
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.length > 0 ? (
                    <>
                      <div>{scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.join(', ')}</div>
                      <div
                        className="cursor-pointer flex space-x-0.5 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(scaleSpec.runtimeEnvs[0].systemEnv.extraPorts.join(','))
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        Copy
                      </div>
                    </>
                  ) : (
                    <div>-</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-4 grid grid-cols-4 space-x-4">
              <div className="col-span-4">
                <div className="t-xs text-secondary">
                  <Trans i18nKey="scalingTarget.cmd" ns="scaling">
                    Start Command
                  </Trans>
                </div>
                <div className="t-sb text-primary flex space-x-1 items-center">
                  {scaleSpec.runtimeEnvs[0].systemEnv.cmd.length > 0 ? (
                    <>
                      <div>{scaleSpec.runtimeEnvs[0].systemEnv.cmd}</div>
                      <div
                        className="cursor-pointer flex space-x-0.5 items-center t-sm text-highlight"
                        onClick={() => {
                          copyToClipboard(scaleSpec.runtimeEnvs[0].systemEnv.cmd)
                        }}
                      >
                        <CopyIcon width={16} height={16} />
                        Copy
                      </div>
                    </>
                  ) : (
                    <>-</>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
